import React from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle, LogOut, ChevronDown, Settings, FileText, Package, MessageCircle, ShoppingCart, ClipboardList, Heart, Briefcase, Mail, Building2, Car, MapPin, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import NotificationBell from '../NotificationBell';
import { getUserAvatarUrl } from '../../utils/userAvatar';
import { formatUserRoleLabel } from '../../utils/userRole';

const DesktopHeader = ({ data }) => {
  const {
    navbarRef, bannerHeight, navbarVisible,
    logoUrl, isDefaultLogo, siteName,
    selectedProvince, setSelectedProvince, provinces,
    navItems, isActive, user,
    handleLogout, handleLogin,
  } = data;

  const LogoImg = ({ className }) => (!isDefaultLogo && logoUrl)
    ? <img loading="eager" decoding="async" fetchPriority="high" src={logoUrl} alt={siteName || ''} className={className} />
    : <span className="font-bold text-xl text-emerald-900">{siteName}</span>;

  return (
    <header
      ref={navbarRef}
      style={{ top: bannerHeight }}
      className={`hidden md:block fixed left-0 right-0 z-50 bg-white backdrop-blur-lg border-b border-stone-200 shadow-sm transition-all duration-300 h-[56px] ${navbarVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      <div className="container-app h-full">
        <div className="flex items-center justify-between h-full">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <LogoImg className="h-6 w-auto" />
          </Link>

          {/* Region Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-semibold text-emerald-900 hover:bg-stone-100 shrink-0" data-testid="province-selector">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span className="max-w-[100px] truncate">{selectedProvince?.name || 'All UK'}</span>
                <ChevronDown className="w-3.5 h-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
              <DropdownMenuItem onClick={() => setSelectedProvince(null)} className={`cursor-pointer ${!selectedProvince ? 'bg-orange-50 text-orange-700 font-semibold' : ''}`} data-testid="province-all">
                <MapPin className="w-4 h-4 mr-2" />All UK
              </DropdownMenuItem>
              {provinces.map(prov => (
                <DropdownMenuItem key={prov.region_id} onClick={() => setSelectedProvince(prov)} className={`cursor-pointer ${selectedProvince?.region_id === prov.region_id ? 'bg-orange-50 text-orange-700 font-semibold' : ''}`} data-testid={`province-${prov.plate_code}`}>
                  <MapPin className="w-4 h-4 mr-2" />{prov.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center mx-2">
            {navItems.slice(0, 8).map((item) => (
              <Link key={item.href} to={item.href} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.href) ? 'bg-emerald-900/10 text-emerald-900' : 'text-stone-600 hover:bg-stone-100'}`} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            <Link to="/post-listing">
              <Button className="btn-secondary hidden sm:flex" data-testid="create-listing-btn">
                <PlusCircle className="w-4 h-4 mr-2" />Post Listing
              </Button>
            </Link>

            {user ? (
              <>
                {(user.role === 'provider' || user.is_provider) && (
                  <Link to="/provider-dashboard" className="hidden sm:flex">
                    <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="provider-panel-btn">
                      <Briefcase className="w-4 h-4 mr-2" />Provider Panel
                    </Button>
                  </Link>
                )}
                {user.is_realtor && (
                  <Link to="/agent-dashboard" className="hidden sm:flex">
                    <Button variant="outline" size="sm" className="border-teal-500 text-teal-600 hover:bg-teal-50" data-testid="realtor-panel-btn">
                      <Building2 className="w-4 h-4 mr-2" />Estate Agent Panel
                    </Button>
                  </Link>
                )}
                {user.is_gallery_dealer && (
                  <Link to="/dealer-dashboard" className="hidden sm:flex">
                    <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="gallery-dealer-panel-btn">
                      <Car className="w-4 h-4 mr-2" />Dealer Panel
                    </Button>
                  </Link>
                )}
                <NotificationBell user={user} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative p-2 hover:bg-stone-100 rounded-full" data-testid="user-menu-btn">
                      <Avatar className="h-8 w-8 border-2 border-transparent hover:border-orange-600 transition-colors">
                        <AvatarImage src={getUserAvatarUrl(user)} />
                        <AvatarFallback className="bg-emerald-900 text-white text-sm font-medium">{user.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <div className="px-3 py-3 bg-gradient-to-r from-emerald-900 to-emerald-800 rounded-t-md -mt-1 -mx-1">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border-2 border-white/30"><AvatarImage src={getUserAvatarUrl(user)} /><AvatarFallback className="bg-white/20 text-white">{user.name?.charAt(0)}</AvatarFallback></Avatar>
                        <div><p className="text-sm font-semibold text-white">{user.name}</p><p className="text-xs text-white/70">{user.email}</p></div>
                      </div>
                      <Badge className="mt-2 bg-white/20 text-white text-xs">
                        {formatUserRoleLabel(user)}
                      </Badge>
                    </div>
                    <div className="py-2">
                      <div className="px-2 py-1"><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">My Account</p></div>
                      <DropdownMenuItem asChild><Link to="/provider-dashboard" className="flex items-center cursor-pointer" data-testid="menu-dashboard"><Home className="w-4 h-4 mr-3 text-slate-500" /><span>Dashboard</span></Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/my-listings" className="flex items-center cursor-pointer" data-testid="menu-listings"><FileText className="w-4 h-4 mr-3 text-slate-500" /><span>My Listings</span></Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/my-requests" className="flex items-center cursor-pointer" data-testid="menu-requests"><ClipboardList className="w-4 h-4 mr-3 text-slate-500" /><span>My Requests</span></Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/my-purchases" className="flex items-center cursor-pointer" data-testid="menu-purchases"><ShoppingCart className="w-4 h-4 mr-3 text-slate-500" /><span>Purchases</span></Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/favourites" className="flex items-center cursor-pointer" data-testid="menu-favorites"><Heart className="w-4 h-4 mr-3 text-slate-500" /><span>Favourites</span></Link></DropdownMenuItem>
                    </div>
                    <DropdownMenuSeparator />
                    <div className="py-2">
                      <div className="px-2 py-1"><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Settings</p></div>
                      <DropdownMenuItem asChild><Link to="/my-documents" className="flex items-center cursor-pointer" data-testid="menu-documents"><FileText className="w-4 h-4 mr-3 text-slate-500" /><span>My Documents</span></Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/packages" className="flex items-center cursor-pointer" data-testid="menu-packages"><Package className="w-4 h-4 mr-3 text-slate-500" /><span>Membership Plans</span></Link></DropdownMenuItem>
                    </div>
                    {(user.role === 'provider' || user.role === 'admin' || user.role === 'superadmin') && (<><DropdownMenuSeparator /><div className="py-2"><DropdownMenuItem asChild><Link to="/provider-dashboard" className="flex items-center cursor-pointer text-emerald-600" data-testid="menu-provider"><Briefcase className="w-4 h-4 mr-3" /><span className="font-medium">Provider Panel</span></Link></DropdownMenuItem></div></>)}
                    {user.is_realtor && (<><DropdownMenuSeparator /><div className="py-2"><DropdownMenuItem asChild><Link to="/agent-dashboard" className="flex items-center cursor-pointer text-teal-600" data-testid="menu-realtor"><Building2 className="w-4 h-4 mr-3" /><span className="font-medium">Estate Agent Panel</span></Link></DropdownMenuItem></div></>)}
                    {user.is_gallery_dealer && (<><DropdownMenuSeparator /><div className="py-2"><DropdownMenuItem asChild><Link to="/dealer-dashboard" className="flex items-center cursor-pointer text-emerald-700" data-testid="menu-gallery-dealer"><Car className="w-4 h-4 mr-3" /><span className="font-medium">Dealer Panel</span></Link></DropdownMenuItem></div></>)}
                    {(user.role === 'admin' || user.role === 'superadmin') && (<><DropdownMenuSeparator /><div className="py-2"><DropdownMenuItem asChild><Link to="/admin" className="flex items-center cursor-pointer text-[#f97316]" data-testid="menu-admin"><Settings className="w-4 h-4 mr-3" /><span className="font-medium">Admin Panel</span></Link></DropdownMenuItem></div></>)}
                    <DropdownMenuSeparator />
                    <div className="py-2">
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50" data-testid="menu-logout"><LogOut className="w-4 h-4 mr-3" /><span>Sign Out</span></DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button onClick={handleLogin} className="btn-primary" data-testid="login-btn">Sign In</Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DesktopHeader;
